<!-- questions-frontend/src/components/TaskCenter.vue -->
<template>
  <div>
    <!-- 悬浮按钮：仅无导航栏页面（如文档详情）显示 -->
    <div v-if="showFloating" class="task-center-fab">
      <el-badge :value="activeCount" :hidden="activeCount === 0">
        <el-button circle type="primary" :icon="List" @click="open" />
      </el-badge>
    </div>

    <el-drawer
      :model-value="visible"
      title="任务中心"
      direction="rtl"
      size="380px"
      :destroy-on-close="false"
      @update:model-value="onDrawer"
    >
      <div class="filter-bar">
        <el-radio-group :model-value="typeFilter" size="small" @change="setFilter">
          <el-radio-button label="all">全部</el-radio-button>
          <el-radio-button label="document-download">下载</el-radio-button>
          <el-radio-button label="scan-pdf-batch">批量导入</el-radio-button>
          <el-radio-button label="review-question-generation">复习题</el-radio-button>
        </el-radio-group>
      </div>

      <div v-if="tasks.length === 0" class="empty">暂无任务</div>
      <div v-else class="task-list">
        <div v-for="task in tasks" :key="task.id" class="task-item" :class="task.status">
          <div class="task-header">
            <span class="task-title">{{ task.title }}</span>
            <el-tag :type="statusTagType(task.status)" size="small">{{ statusText(task.status) }}</el-tag>
          </div>
          <div v-if="task.status === 'processing'" class="task-progress">
            <el-progress :percentage="task.percent" :stroke-width="6" />
            <div class="task-message">{{ task.message || '处理中...' }}</div>
          </div>
          <div v-else-if="task.status === 'failed' || task.status === 'interrupted'" class="task-error">
            <span>{{ task.error || '任务失败' }}</span>
          </div>
          <div class="task-actions">
            <el-button
              v-if="task.type === 'document-download' && task.status === 'success' && task.result?.fileName"
              type="primary" link size="small" @click="download(task)">下载文件</el-button>
            <el-button
              v-if="task.type === 'scan-pdf-batch' && task.status === 'success'"
              type="primary" link size="small" @click="openBatch(task)">打开</el-button>
            <el-button
              v-if="task.type === 'review-question-generation' && task.status === 'success' && task.result?.file_id"
              type="primary" link size="small" @click="openReviewDocument(task)">打开</el-button>
            <el-button
              v-if="task.type !== 'review-question-generation' && ['failed','interrupted'].includes(task.status)"
              type="warning" link size="small" @click="retry(task)">重试</el-button>
            <el-button
              v-if="task.type !== 'review-question-generation' && ['queued','processing'].includes(task.status)"
              link size="small" @click="cancel(task)">取消</el-button>
            <el-button link size="small" @click="remove(task)">移除</el-button>
          </div>
        </div>
      </div>
    </el-drawer>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useStore } from 'vuex'
import { List } from '@element-plus/icons-vue'
import { ElMessage } from 'element-plus'

const store = useStore()
const route = useRoute()
const router = useRouter()

const visible = computed(() => store.state.tasks.centerVisible)
const typeFilter = computed(() => store.state.tasks.typeFilter)
const tasks = computed(() => store.getters['tasks/filteredTasks'])
const allTasks = computed(() => store.getters['tasks/allTasks'])
const downloadTasks = computed(() => store.state.downloadTasks?.tasks || [])

// 下载任务创建后先写入 downloadTasks store；任务中心列表要等打开抽屉才拉取。
// 角标必须同时看这两份数据，否则新任务要等展开任务中心后才显示。
const activeCount = computed(() => {
  const ids = new Set()
  for (const task of allTasks.value) {
    if (task.status === 'queued' || task.status === 'processing') ids.add(String(task.id))
  }
  for (const task of downloadTasks.value) {
    if (['pending', 'running', 'queued', 'processing'].includes(task.status)) ids.add(String(task.id))
  }
  return ids.size
})
const showFloating = computed(() => {
  if (route.name === 'CheckInReview') return false
  if (route.name === 'LearningReportPrototype') return allTasks.value.length > 0
  return route.meta.hideLayout === true
})

function open() { store.dispatch('tasks/openCenter') }
function onDrawer(v) { if (!v) store.dispatch('tasks/closeCenter') }
function setFilter(v) { store.dispatch('tasks/setTypeFilter', v) }

function statusTagType(s) {
  return ({ queued: 'info', processing: 'warning', success: 'success', failed: 'danger', interrupted: 'danger', cancelled: 'info' })[s] || 'info'
}
function statusText(s) {
  return ({ queued: '排队中', processing: '进行中', success: '已完成', failed: '失败', interrupted: '已中断', cancelled: '已取消' })[s] || s
}

async function download(task) {
  try {
    await store.dispatch('downloadTasks/downloadFile', { taskId: task.id, fileName: task.result.fileName })
  } catch (e) { ElMessage.error(e?.message || '文件不可用，可能已过期，请重新生成') }
}
async function retry(task) {
  try { await store.dispatch('tasks/retry', task.id); ElMessage.success('已重新排队') }
  catch (e) { ElMessage.error(e?.response?.data?.message || e?.message || '重试失败') }
}
async function cancel(task) { await store.dispatch('tasks/cancel', task.id) }
async function remove(task) { await store.dispatch('tasks/remove', task.id) }

function openBatch(task) {
  // 通过全局事件让试卷页打开批量预览（见 Task 12）；不在试卷页时提示
  window.dispatchEvent(new CustomEvent('open-scan-batch', { detail: { batchId: task.refId } }))
  store.dispatch('tasks/closeCenter')
}

async function openReviewDocument(task) {
  await router.push({ name: 'Document', params: { id: task.result.file_id } })
  store.dispatch('tasks/closeCenter')
}
</script>

<style scoped>
.task-center-fab { position: fixed; bottom: 24px; right: 24px; z-index: 9999; }
.task-center-fab :deep(.el-button.is-circle) { width: 48px; height: 48px; padding: 0; font-size: 20px; }
.filter-bar { margin-bottom: 12px; }
.empty { color: var(--el-text-color-secondary); text-align: center; padding: 40px 0; }
.task-list { display: flex; flex-direction: column; gap: 12px; }
.task-item { padding: 12px; border-radius: 8px; background: var(--el-fill-color-light); border: 1px solid var(--el-border-color-lighter); }
.task-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px; }
.task-title { font-size: 14px; font-weight: 500; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; max-width: 240px; }
.task-message { font-size: 12px; color: var(--el-text-color-secondary); margin-top: 6px; }
.task-error { font-size: 12px; color: var(--el-color-danger); margin: 4px 0; }
.task-actions { margin-top: 8px; display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
</style>
